import { Vec2 } from '../utils/math';

export interface QuadtreeItem {
  x: number;
  y: number;
  width: number;
  height: number;
  data: unknown;
}

interface QuadBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Quadtree for spatial queries on snap targets (beam segments, focal points, etc).
 */
export class Quadtree {
  private bounds: QuadBounds;
  private maxObjects: number;
  private maxLevels: number;
  private level: number;
  private items: QuadtreeItem[] = [];
  private nodes: Quadtree[] = [];

  constructor(bounds: QuadBounds, maxObjects: number = 10, maxLevels: number = 5, level: number = 0) {
    this.bounds = bounds;
    this.maxObjects = maxObjects;
    this.maxLevels = maxLevels;
    this.level = level;
  }

  clear(): void {
    this.items = [];
    this.nodes = [];
  }

  insert(item: QuadtreeItem): void {
    if (this.nodes.length > 0) {
      const indices = this.getIndices(item);
      for (const idx of indices) {
        this.nodes[idx].insert(item);
      }
      return;
    }

    this.items.push(item);

    if (this.items.length > this.maxObjects && this.level < this.maxLevels) {
      if (this.nodes.length === 0) {
        this.split();
      }
      for (const existingItem of this.items) {
        const indices = this.getIndices(existingItem);
        for (const idx of indices) {
          this.nodes[idx].insert(existingItem);
        }
      }
      this.items = [];
    }
  }

  /** Query all items that overlap with the given bounds */
  query(bounds: QuadBounds): QuadtreeItem[] {
    const result: QuadtreeItem[] = [];
    this.queryInternal(bounds, result);
    return result;
  }

  /** Query items near a point within a radius */
  queryRadius(center: Vec2, radius: number): QuadtreeItem[] {
    return this.query({
      x: center.x - radius,
      y: center.y - radius,
      width: radius * 2,
      height: radius * 2,
    });
  }

  private queryInternal(bounds: QuadBounds, result: QuadtreeItem[]): void {
    if (!this.intersects(this.bounds, bounds)) return;

    for (const item of this.items) {
      if (this.intersects(item, bounds)) {
        result.push(item);
      }
    }

    for (const node of this.nodes) {
      node.queryInternal(bounds, result);
    }
  }

  private split(): void {
    const hw = this.bounds.width / 2;
    const hh = this.bounds.height / 2;
    const x = this.bounds.x;
    const y = this.bounds.y;

    this.nodes = [
      new Quadtree({ x: x + hw, y, width: hw, height: hh }, this.maxObjects, this.maxLevels, this.level + 1),    // NE
      new Quadtree({ x, y, width: hw, height: hh }, this.maxObjects, this.maxLevels, this.level + 1),             // NW
      new Quadtree({ x, y: y + hh, width: hw, height: hh }, this.maxObjects, this.maxLevels, this.level + 1),     // SW
      new Quadtree({ x: x + hw, y: y + hh, width: hw, height: hh }, this.maxObjects, this.maxLevels, this.level + 1), // SE
    ];
  }

  private getIndices(item: QuadtreeItem): number[] {
    const indices: number[] = [];
    const midX = this.bounds.x + this.bounds.width / 2;
    const midY = this.bounds.y + this.bounds.height / 2;

    const touchesLeft = item.x < midX;
    const touchesRight = item.x + item.width > midX;
    const touchesTop = item.y < midY;
    const touchesBottom = item.y + item.height > midY;

    if (touchesRight && touchesTop) indices.push(0);  // NE
    if (touchesLeft && touchesTop) indices.push(1);   // NW
    if (touchesLeft && touchesBottom) indices.push(2); // SW
    if (touchesRight && touchesBottom) indices.push(3); // SE

    return indices;
  }

  private intersects(a: QuadBounds, b: QuadBounds): boolean {
    return !(a.x > b.x + b.width ||
             a.x + a.width < b.x ||
             a.y > b.y + b.height ||
             a.y + a.height < b.y);
  }
}
